import React, { useRef, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { color } from '../styles'
import { Icon } from './Icon'
import { ASSET_URL, ASSET_URL_V2 } from '../config/env'
import { utcToMilitaryDisplayTime, utcToDisplayDate } from '../util/datetime'
import { Tooltip } from './Tooltip'
import { Loading } from './Icon/antDirectory'

type Props = {
  path?: string
  fileName?: string | { name: string; cameraId: string }
  filePath?: string
  className?: string
  description: string
  disableClick?: boolean
  disableBackground?: boolean
  objectFit?: 'cover' | 'contain'
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  timezone?: string
}

const S3_URL = '//navtrac-image-bucket-hk.s3.ap-east-1.amazonaws.com'
const S3_URL_V2 = '//navtrac-image-bucket-west-1.s3.us-west-1.amazonaws.com'

const generatePreview = (
  path?: string,
  filename?: string | { name: string; cameraId: string },
  filepath?: string
): string | null => {
  let newFileName = null

  if ((!path || !filename) && !filepath) {
    return null
  }

  if (filepath.includes('jpg') || filepath.includes('jpg_t')) {
    return `${ASSET_URL_V2}/${filepath}`.replace('jpg_t', 'jpg')
  }

  if (filepath) {
    return `${ASSET_URL}/${filepath}.jpg`
  }

  if (typeof filename !== 'string') {
    newFileName = `${filename.name.split('/')[2]}_${filename.name.split('/')[3]}`
  } else {
    newFileName = filename
  }

  // ws_store/0012/210301/190139
  const camera = newFileName.split('_')[2]

  if (!camera) return null

  return `${ASSET_URL}/${path}/${camera}/${filename}.jpg`
}

const getTimeFromFileName = (filename: string | { name: string; cameraId: string }) => {
  let newFileName = null

  if (typeof filename !== 'string') {
    newFileName = `${filename.name.split('/')[2]}_${filename.name.split('/')[3]}`
  } else {
    newFileName = filename
  }

  const dateParts = newFileName.split('_')
  const imageDateTime = new Date(
    Date.UTC(
      parseInt(`20${dateParts[0].substring(0, 2)}`),
      parseInt(dateParts[0].substring(2, 4)) - 1,
      parseInt(dateParts[0].substring(4, 6)),
      parseInt(dateParts[1].substring(0, 2)),
      parseInt(dateParts[1].substring(2, 4)),
      parseInt(dateParts[1].substring(4, 6))
    )
  )
  return imageDateTime
}

export const ImageCanvas = (props: Props): JSX.Element => {
  const {
    path,
    fileName,
    filePath,
    className,
    description,
    disableClick,
    objectFit = 'cover',
    disableBackground,
    onClick,
    timezone,
  } = props

  const imageDateTime = fileName ? getTimeFromFileName(fileName).toISOString() : null
  const canvasRef = useRef(null)
  const downloadCanvasRef = useRef(null)
  const imageRef = useRef(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const src = generatePreview(path, fileName, filePath)

  const newFileName = typeof fileName !== 'string' ? fileName?.name : fileName

  useEffect(() => {
    const canvas = canvasRef.current
    const img = new Image()

    const drawImageDatetime = () => {
      const date = utcToDisplayDate(imageDateTime, timezone, 'YYYY-MM-DD')
      const time = utcToMilitaryDisplayTime(imageDateTime, timezone)
      const imageCoordinates = imageRef.current?.getBoundingClientRect()

      if (canvas && imageCoordinates) {
        canvas.width = 500
        canvas.height = 300
        const context = canvas.getContext('2d')
        context.clearRect(0, 0, canvas.width, canvas.height)
        context.drawImage(img, 0, 0)
        context.font = `18pt 'Courier New', monospace`
        context.fillStyle = 'white'
        context.fillText(`${date} ${time}`, imageCoordinates.x, imageCoordinates.y + 80)
      }
    }

    const resizeObserver = new ResizeObserver(() => {
      drawImageDatetime()
    })

    if (imageRef.current) {
      resizeObserver.observe(imageRef.current)
    }

    drawImageDatetime()

    return () => {
      resizeObserver.disconnect()
    }
  }, [canvasRef, imageRef, imageDateTime, timezone, src])

  const onDownload = () => {
    setIsDownloading(true)
    const image = imageRef.current
    const canvas = downloadCanvasRef.current
    const img = new Image()
    img.setAttribute('crossOrigin', 'anonymous')
    img.src = image.src.replace(ASSET_URL, S3_URL)

    if (imageRef.current.src.includes('img.navtrac.com')) {
      img.src = image.src.replace(ASSET_URL_V2, S3_URL_V2)
    }

    img.onload = () => {
      if (canvas) {
        canvas.width = 1920
        canvas.height = 1080
        const context = canvas.getContext('2d')
        context.drawImage(img, 0, 0)
        context.font = `24pt 'Courier New', monospace`
        context.fillStyle = 'white'
        context.fillText(
          `${utcToDisplayDate(imageDateTime, timezone)} ${utcToMilitaryDisplayTime(
            imageDateTime,
            timezone
          )}`,
          5,
          110
        )
      }

      const anchor = document.createElement('a')
      anchor.download = newFileName ? `${newFileName}.jpg` : 'image.jpg'
      anchor.href = canvas.toDataURL('image/jpg')
      anchor.click()
      setIsDownloading(false)
    }
  }
  return (
    <Container
      className={className}
      disabled={disableClick}
      onClick={(e) => {
        if (disableClick) return
        onClick?.(e)
      }}
      disableBackground={Boolean(disableBackground)}
      key={src}
    >
      {src ? (
        <div>
          <Content ref={imageRef} alt={description} src={src} objectFit={objectFit} />
          <Canvas ref={canvasRef} id={newFileName} />
          <DownloadCanvas ref={downloadCanvasRef} id={`${newFileName}-download`} />
          <DownloadContainer>
            {!isDownloading ? (
              <Tooltip tipText="Download">
                <Icon iconKey="Download" color="danger" size={8} onClick={() => onDownload()} />
              </Tooltip>
            ) : (
              <Icon iconKey="Loading" color="danger" size={8} />
            )}
          </DownloadContainer>
        </div>
      ) : (
        <Icon size={6} iconKey="FileUnknown" />
      )}
    </Container>
  )
}

const Container = styled.div<{ disabled?: boolean; disableBackground: boolean }>`
  display: flex;
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  background-color: ${color.background.app};
  ${(p) => p.disableBackground && 'background-color: transparent;'}
  ${(p) =>
    p.disabled
      ? ''
      : css`
          cursor: pointer;
        `}
`
const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
`

const DownloadCanvas = styled.canvas`
  display: none;
`

const Content = styled.img<{ objectFit: 'cover' | 'contain' }>`
  width: 100%;
  height: 100%;
  object-fit: ${(p) => p.objectFit};
  z-index: 2;
`

const DownloadContainer = styled.div`
  position: absolute;
  top: 2%;
  left: 5px;
  z-index: 3;
`

ImageCanvas.Content = Content
ImageCanvas.Container = Container
